import { useInfoPanelDetails } from 'gcs-common/clients/api/entities/channels/channel-api';
import { useGetCurrentUser } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import InfoPanelHeader from 'gcs-common/components/InfoPanel/InfoPanelHeader';
import { AccordionList, ContactList } from 'gcs-common/components/InfoPanel/ListComponents/ContactList';
import {
  AddMembersItem,
  ArchiveItem,
  MailItem,
  UpdateChannelName,
} from 'gcs-common/components/InfoPanel/ListComponents/ListItem';
import { CHANNEL_TYPE } from 'gcs-common/helper/channelTypeHelper';
import FEATURE_FLAGS from 'gcs-common/helper/featureFlagsHelper';
import { getHasFeatureFlag } from 'gcs-common/slices/currentUser/currentUserSelectors';
import type { components } from 'gcs-common/types/openapi';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import AddMembersModal from '../AddMembersModal/AddMembersModal';
import UpdateChannelNameModal from '../UpdateChannelNameModal/UpdateChannelNameModal';
import PanelItemsSection from './ListComponents/PanelItemsSection';

type UserWithHouseOrCustomerDTO = components['schemas']['UserWithHouseOrCustomerDTO'];

interface ChatInfoPanelProps {
  onClose: VoidFunction;
}

const ChatInfoPanel = (props: ChatInfoPanelProps) => {
  const { onClose } = props;
  const isMultipleCraftsmenEnabled = useSelector(
    getHasFeatureFlag(FEATURE_FLAGS.AGENTS_CHAT_CREATION_WITH_MULTIPLE_CRAFTSMEN),
  );
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [updateChannelNameOpen, setUpdateChannelNameOpen] = useState(false);
  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();
  const { data: currentUser } = useGetCurrentUser();
  const { data: infoPanelData } = useInfoPanelDetails(selectedChannelId);
  const isCurrentUserAgent = currentUser?.isAgent || false;
  const isCurrentUserMemberOfChannel = infoPanelData?.users?.some((user) => user.id === currentUser?.id) || false;
  const promoEmail = infoPanelData?.memberEmail;
  const allowedAgents = infoPanelData?.assignedAgentUsers.concat(infoPanelData?.assignedTeamUsers) ?? [];

  // if the user belongs to the channel's primary house, return the primary house, otherwise return the first house
  const selectHouse = (user: UserWithHouseOrCustomerDTO) => {
    return user.houses?.find((house) => house.houseId === infoPanelData?.primaryHouseId) ?? user.houses?.[0];
  };

  const groupUsers = (users: UserWithHouseOrCustomerDTO[]) => {
    return users.reduce(
      (acc, user) => {
        if (user.userType === 'AGENT') {
          // if the user has multiple houses, select the primary house if possible, otherwise select the first house
          const house = selectHouse(user) ?? { houseId: '0', friendlyHouseName: 'No House Found for Agent' };
          if (!acc.houses[house.houseId]) {
            acc.houses[house.houseId] = [];
          }
          const userWithSingleHouse = { ...user, house: [house] };
          acc.houses[house.houseId]?.push(userWithSingleHouse);
        } else if (user.userType === 'CRAFTSMAN') {
          const customerNo = user.customerNo ?? '-1';
          if (!acc.companies[customerNo]) {
            acc.companies[customerNo] = [];
          }
          acc.companies[customerNo]?.push(user);
        }
        return acc;
      },
      { houses: {}, companies: {} } as {
        houses: Record<string, UserWithHouseOrCustomerDTO[]>;
        companies: Record<string, UserWithHouseOrCustomerDTO[]>;
      },
    );
  };

  const groupedUsers = groupUsers(infoPanelData?.users ?? []);

  return (
    <div className="pb-10">
      <AddMembersModal isOpen={addMembersOpen} onRequestClose={() => setAddMembersOpen(false)} />
      <UpdateChannelNameModal isOpen={updateChannelNameOpen} onRequestClose={() => setUpdateChannelNameOpen(false)} />
      <InfoPanelHeader
        userIds={infoPanelData?.users?.map((user) => user.id).filter((id) => id !== currentUser?.id) ?? []}
        isCurrentUserAgent={isCurrentUserAgent}
        channelId={selectedChannelId}
        onClose={onClose}
      />
      <div className="px-4">
        <div className="flex-col space-y-2">
          <PanelItemsSection>
            {isMultipleCraftsmenEnabled &&
              isCurrentUserAgent &&
              infoPanelData?.channelType === CHANNEL_TYPE.SALES_SPACE && (
                <div>
                  <UpdateChannelName onClick={() => setUpdateChannelNameOpen(true)} />
                </div>
              )}
            {isMultipleCraftsmenEnabled &&
              isCurrentUserAgent &&
              infoPanelData?.channelType === CHANNEL_TYPE.SALES_SPACE && (
                <div>
                  <AddMembersItem onClick={() => setAddMembersOpen(true)} />
                </div>
              )}
            <div>
              <ArchiveItem />
            </div>
            {isCurrentUserMemberOfChannel && isCurrentUserAgent && (
              <div>
                <MailItem email={promoEmail ?? ''} />
              </div>
            )}
          </PanelItemsSection>
          {isCurrentUserMemberOfChannel && isCurrentUserAgent ? (
            <div className="text-neutral-11 text-sm">
              Verwende diese E-Mail, um Angebote von WW90 in den Chat zu senden.
            </div>
          ) : (
            <div />
          )}
        </div>
        <div>
          {Object.values(groupedUsers.companies).map((usersOfCompany) => {
            const customerNo = usersOfCompany[0]?.customerNo ?? '-1';
            const customerFriendlyName = usersOfCompany[0]?.customerFriendlyName ?? 'no customer name found';
            const customerUserIds = usersOfCompany.map((user) => user.id);
            return (
              <ContactList
                key={`groupchat-contactlist-${customerNo}`}
                listTitle={customerFriendlyName}
                listSecondTitle={customerNo}
                contactIds={customerUserIds}
                channelId={selectedChannelId}
                agentView={isCurrentUserAgent}
              />
            );
          })}

          {infoPanelData?.channelType === CHANNEL_TYPE.INTERNAL_AGENT ? (
            <ContactList
              listTitle={''}
              contactIds={infoPanelData.users.map((user) => user.id)}
              channelId={selectedChannelId}
              agentView={isCurrentUserAgent}
            />
          ) : (
            Object.entries(groupedUsers.houses).map(([houseId, usersOfHouse]) => {
              const firstUser = usersOfHouse[0];
              const house = firstUser?.houses?.find((house) => house.houseId === houseId) ?? firstUser?.houses?.[0];
              const userIds = usersOfHouse.map((user) => user.id);
              return (
                <ContactList
                  key={`groupchat-contactlist-${house?.houseId}`}
                  listTitle={house?.friendlyHouseName ?? ''}
                  contactIds={userIds}
                  channelId={selectedChannelId}
                  agentView={isCurrentUserAgent}
                />
              );
            })
          )}

          {infoPanelData?.channelType === 'sales_space' && isCurrentUserAgent && allowedAgents.length > 0 && (
            <AccordionList
              key={`groupchat-accordionlist-${selectedChannelId}`}
              contactIds={allowedAgents.map((user) => user.id) ?? []}
              channelId={selectedChannelId}
              showInfoIcon={false}
              hideStatus={true}
              agentView={isCurrentUserAgent}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInfoPanel;
